import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import api from "./api.js";
import GlobalLoadingSpinner from "./components/GlobalSpinner";
import { useCompanyApi } from "./hooks/useCompanyApi";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const { updateCompany } = useCompanyApi();

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (session) {
        sessionStorage.setItem("supabase_jwt", session.access_token);
      }
      setLoading(false);
    };

    getSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        if (session) {
          sessionStorage.setItem("supabase_jwt", session.access_token);
        } else {
          sessionStorage.removeItem("supabase_jwt");
        }
        setLoading(false);
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      setSubmitLoading(true);
      setErrors([]);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin + "/callback",
        },
      });
      if (error) {
        throw error;
      }
    } catch (error) {
      setErrors(prev => [...prev, error.message]);
    } finally {
      setSubmitLoading(false);
    }
  };

  const signUpWithEmail = async (email, password) => {
    try {
      setSubmitLoading(true);
      setErrors([]);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      if (data.user) {
        await api.post("/api/create-user", {
          email,
        });

        await updateCompany({
          registration_step: "website-url",
        });

        window.location.href = window.location.origin + "/callback?signup=true";
      }
    } catch (error) {
      setErrors(prev => [
        ...prev,
        error?.response?.data?.detail || error.message,
      ]);
    } finally {
      setSubmitLoading(false);
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      setSubmitLoading(true);
      setErrors([]);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        throw error;
      } else {
        setUser(data.user);
        window.location.href = window.location.origin + "/callback";
      }
    } catch (error) {
      setErrors(prev => [...prev, error.message]);
    } finally {
      setSubmitLoading(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    sessionStorage.removeItem("supabase_jwt");
  };

  if (loading) return <GlobalLoadingSpinner />;

  return (
    <AuthContext.Provider
      value={{
        user,
        signInWithGoogle,
        signUpWithEmail,
        signInWithEmail,
        signOut,
        errors,
        loading: submitLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
